import { cn } from '@amore/ui/cn'
import { Alert } from '@amore/ui/components/alert'
import { DatePicker } from '@amore/ui/components/date-picker'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Textarea } from '@amore/ui/components/textarea'
import { useNavigate } from '@tanstack/react-router'
import {
  CalendarDays,
  Check,
  Flag,
  NotebookPen,
  Sparkles,
  Tag,
  Trash2,
  UserRound,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { daysBetween, newId, relativeDue, TODAY } from '@/lib/format'
import { priorityLabels } from '@/lib/labels'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Task, TaskPriority } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { LinkedRecord } from '@/patterns/linked-record'
import { useToast } from '@/shell/toaster'
import { automaticNotice, knownLabels, taskPayment, taskSource } from './model'
import {
  datePickerLabels,
  draftOf,
  emptyDraft,
  LabelPicker,
  sameDraft,
  type TaskDraft,
} from './task-fields'

const priorities = (['low', 'normal', 'high'] as TaskPriority[]).map((value) => ({
  value,
  label: priorityLabels[value],
}))

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'

/**
 * One sheet for adding and editing a task (no separate dialogs): the name as
 * the heading, the properties with live controls, what the task belongs to,
 * and the done toggle and delete in the footer. Without `task` it adds one.
 */
export function TaskSheet({ task: existing, onClose }: { task?: Task; onClose: () => void }) {
  const isNew = !existing
  const task: Task = existing ?? { id: 'new', ...emptyDraft, done: false }
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [draft, setDraft] = useState(() => draftOf(task))
  const [titleError, setTitleError] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const dirty = !sameDraft(draft, draftOf(task))
  const notice = automaticNotice(task)
  const source = taskSource(task, data)
  const payment = taskPayment(task, data)
  const set = (patch: Partial<TaskDraft>) => setDraft((current) => ({ ...current, ...patch }))

  const save = () => {
    if (!draft.title.trim()) {
      setTitleError(true)
      return
    }
    const title = draft.title.trim()
    update((current) => ({
      ...current,
      tasks: isNew
        ? [{ id: newId('task'), ...draft, title, done: false }, ...current.tasks]
        : replaceById(current.tasks, task.id, { ...draft, title }),
    }))
    showToast({ title: isNew ? 'Úkol přidán' : 'Úkol uložen', tone: 'success' })
    onClose()
  }
  const toggleDone = () => {
    if (task.automatic) {
      showToast({ title: 'Stav se mění automaticky', description: notice ?? undefined })
      return
    }
    update((current) => ({
      ...current,
      tasks: replaceById(current.tasks, task.id, { done: !task.done }),
    }))
    showToast({
      title: task.done ? 'Úkol vrácen mezi nesplněné' : 'Úkol dokončen',
      tone: 'success',
    })
  }
  const remove = () => {
    update((current) => ({
      ...current,
      tasks: current.tasks.filter((item) => item.id !== task.id),
    }))
    showToast({ title: 'Úkol smazán' })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        eyebrow={isNew ? 'Nový úkol' : task.payment ? 'Platba' : 'Úkol'}
        title={task.title || 'Nový úkol'}
        titleContent={
          <div className="space-y-4">
            <div className="space-y-1">
              <textarea
                value={draft.title}
                rows={1}
                data-autofocus={isNew || undefined}
                readOnly={!canEdit}
                aria-label="Název úkolu"
                aria-invalid={titleError || undefined}
                placeholder={isNew ? 'Co je potřeba zařídit?' : 'Název úkolu'}
                onChange={(event) => {
                  set({ title: event.target.value })
                  setTitleError(false)
                }}
                className={cn(
                  'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                  canEdit && 'hover:bg-muted/60 focus:bg-muted/60',
                  task.done && 'text-muted-foreground line-through',
                )}
              />
              {titleError ? (
                <p className="text-sm text-error-foreground">Doplňte název úkolu.</p>
              ) : null}
            </div>
          </div>
        }
        dirty={dirty}
        primaryAction={
          canEdit ? { label: isNew ? 'Přidat úkol' : 'Uložit', onClick: save } : undefined
        }
        footerAccessory={
          canEdit && !isNew ? (
            <DoneButton task={task} canEdit={canEdit} onToggle={toggleDone} />
          ) : null
        }
        destructiveAction={
          canEdit && !isNew
            ? {
                label: 'Smazat úkol',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
      >
        <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
          {notice ? (
            <Alert tone="info" announcement="none" className="flex items-start gap-2.5">
              <Sparkles className="mt-1 size-4 shrink-0" aria-hidden="true" />
              {notice}
            </Alert>
          ) : null}

          <fieldset disabled={!canEdit} className="grid grid-cols-2 gap-x-6 gap-y-7">
            <legend className="sr-only">Vlastnosti úkolu</legend>
            <Tile icon={CalendarDays} label="Termín">
              <DatePicker
                value={draft.dueDate ?? ''}
                onValueChange={(value) => set({ dueDate: value || null })}
                readOnly={!canEdit}
                labels={datePickerLabels}
                className={softControl}
                formatDate={(date) =>
                  date.toLocaleDateString('cs-CZ', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                }
                formatMonth={(date) =>
                  date.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })
                }
              />
              {draft.dueDate ? (
                <span
                  className={cn(
                    'block px-1 text-xs',
                    !task.done && daysBetween(TODAY, draft.dueDate) < 0
                      ? 'font-semibold text-error-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {relativeDue(draft.dueDate)}
                </span>
              ) : null}
            </Tile>

            <Tile icon={Flag} label="Priorita">
              <Segmented
                label="Priorita"
                options={priorities}
                value={draft.priority}
                onChange={(priority) => set({ priority })}
                disabled={!canEdit}
              />
            </Tile>

            <Tile icon={NotebookPen} label="Poznámka" wide>
              <Textarea
                value={draft.note}
                maxLength={3000}
                readOnly={!canEdit}
                placeholder={
                  canEdit ? 'Kontakty, domluvené detaily, na co nezapomenout…' : 'Bez poznámky'
                }
                onChange={(event) => set({ note: event.target.value })}
                className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
              />
            </Tile>

            <Tile icon={UserRound} label="Má na starosti" wide>
              <Select
                variant="soft"
                className={softControl}
                value={draft.assigneeId ?? 'unassigned'}
                onValueChange={(value) =>
                  set({ assigneeId: value === 'unassigned' ? null : value })
                }
              >
                <SelectItem value="unassigned">Nikdo</SelectItem>
                {data.members.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name}
                  </SelectItem>
                ))}
              </Select>
            </Tile>

            <Tile icon={Tag} label="Štítky" wide>
              <LabelPicker
                value={draft.labels}
                available={knownLabels(data.tasks)}
                readOnly={!canEdit}
                onChange={(labels) => set({ labels })}
                compact
              />
            </Tile>
          </fieldset>

          {source ? (
            <section className="space-y-2.5">
              <h3 className="text-sm font-semibold">
                {payment ? 'Platba v rozpočtu' : 'Patří k části plánu'}
              </h3>
              <LinkedRecord
                module={source.module}
                title={source.title}
                meta={payment?.meta}
                actionLabel={source.action}
                onAction={
                  task.payment
                    ? () =>
                        void navigate({
                          to: `/budget/${task.payment?.expenseId}`,
                          search: { tab: 'payments' },
                        })
                    : undefined
                }
              />
            </section>
          ) : null}
        </div>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Smazat úkol?"
        description="Tuto akci nelze vrátit."
        keepLabel="Ponechat úkol"
        confirmLabel="Smazat úkol"
        onConfirm={remove}
      />
    </>
  )
}

/** One property: its label above, its control always live (no extra click to edit). */
function Tile({
  icon: Icon,
  label,
  wide = false,
  children,
}: {
  icon: typeof Flag
  label: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-2.5', wide && 'col-span-2')}>
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4" aria-hidden="true" />
        {label}
      </span>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

/**
 * The task's status as a labelled pill in the footer, next to "Uložit": one click finishes it,
 * another brings it back. Automatic tasks explain themselves instead.
 */
function DoneButton({
  task,
  canEdit,
  onToggle,
}: {
  task: Task
  canEdit: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!canEdit}
      aria-pressed={task.done}
      className={cn(
        'group inline-flex items-center gap-2.5 rounded-full border py-1.5 pr-4 pl-1.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60',
        task.done
          ? 'border-transparent bg-success text-success-foreground'
          : 'border-success-foreground/40 text-foreground hover:bg-success',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-7 place-items-center rounded-full border-2 transition-colors',
          task.done
            ? 'border-success-foreground bg-success-foreground text-background'
            : 'border-success-foreground/60 text-success-foreground group-hover:bg-success-foreground group-hover:text-background',
        )}
      >
        {task.automatic && !task.done ? (
          <Sparkles className="size-3.5" />
        ) : (
          <Check className="size-3.5" />
        )}
      </span>
      {task.done ? (
        <>
          Hotovo
          <span className="font-normal text-muted-foreground group-hover:underline">Vrátit</span>
        </>
      ) : task.automatic ? (
        'Splní se automaticky'
      ) : (
        'Označit jako hotové'
      )}
    </button>
  )
}
